import { useTranslation } from "react-i18next";
import { motion, useInView } from "framer-motion";
import { useRef } from "react";

export default function About() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language?.startsWith("ar");
  const aboutLabel = t("about.label").trim();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="about" className="w-full py-16 md:py-24 bg-[#F8F7F5]">
      <div ref={ref} className="w-full px-4 md:px-8 lg:px-12">
        <div className="mb-12 md:mb-16">
          {aboutLabel && (
            <motion.span
              className="text-sm font-medium text-[#8B7355] tracking-wide uppercase mb-4 block"
              initial={{ opacity: 0, y: 20 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
              transition={{ duration: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {aboutLabel}
            </motion.span>
          )}

          <h2 className="text-4xl md:text-6xl lg:text-7xl font-bold text-stone-900 leading-tight">
            <motion.span
              className="block"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.1, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t("about.line1")}
            </motion.span>
            <motion.span
              className="block italic font-serif text-stone-700"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.2, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t("about.line2")}
            </motion.span>
            <motion.span
              className="block"
              initial={{ opacity: 0, y: 40 }}
              animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
              transition={{ duration: 0.6, delay: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
            >
              {t("about.line3")}
            </motion.span>
          </h2>

          <motion.p
            className="mt-4 text-base md:text-lg text-stone-600"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.4, ease: [0.25, 0.1, 0.25, 1] }}
          >
            {t("about.subtitle")}
          </motion.p>

          <motion.a
            href="/services"
            className="mt-6 inline-flex items-center gap-2 bg-brand text-white font-semibold px-6 py-3 rounded-full hover:bg-brand-dark transition-colors whitespace-nowrap cursor-pointer"
            initial={{ opacity: 0, y: 20 }}
            animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 20 }}
            transition={{ duration: 0.5, delay: 0.5, ease: [0.25, 0.1, 0.25, 1] }}
            whileHover={{ scale: 1.03 }}
            whileTap={{ scale: 0.98 }}
          >
            {t("about.servicesButton")}
            <span className="w-6 h-6 flex items-center justify-center bg-white/20 rounded-full">
              <i className={`ri-${isRTL ? "arrow-left-line" : "arrow-right-line"} text-xs`} />
            </span>
          </motion.a>
        </div>
      </div>
    </section>
  );
}